import Autocomplete from "@mui/lab/Autocomplete";
import { TextField } from "@mui/material";
import { AutoCompleteStyled } from "components/form/AutoCompleteForm/AutoCompleteForm.style";
import { getValueFromOptions } from "components/form/AutoCompleteForm/utils/AutoComplete.utils";
import { useApi } from "hooks/useApi/useApiHooks";
import { CACHE_REFERENCE, STALE_REFERENCE } from "services/client/query.config";
import useDebounce from "hooks/useDebounce";
import useAllQueryParams from "hooks/useGetAllQueryParams/useAllQueryParams";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { IOption } from "types/form.types";

interface IAutocompleteFilter {
  filterName: string;
  options?: IOption[];
  optionsUrl?: string;
  returnOnlyId?: boolean;
  label?: string;
  placeholder?: string | number;
  disabled?: boolean;
  nameProp?: string;
  exQueryParams?: object;
  dataProp?: string;
  multiple?: boolean;
  mapData?: (options: IOption[]) => IOption[];
  onChange?: (options: IOption | null | undefined) => void;
}

function AutoCompleteFilter({
  options = [],
  optionsUrl = "", // 😐
  returnOnlyId = true,
  label = "",
  disabled,
  placeholder,
  filterName,
  nameProp = "name",
  exQueryParams = {},
  dataProp = "data.data" as const, //😐
  mapData,
  onChange,
  multiple = false,
}: IAutocompleteFilter) {
  const [queryParams, setQueryParams] = useState<{ search?: string }>();
  const [search, setSearch] = useState<string>();
  const { debouncedValue: debVal, isDebouncing } = useDebounce(search, 600);
  const [value, setValue] = useState<string | undefined | null>(null);
  const [multipleValue, setMultipleValue] = useState<IOption<string>[]>([]);

  useEffect(() => {
    setQueryParams({
      search,
    });
  }, [debVal]);

  const { data: OptionsData, isFetching } = useApi<IOption[]>(
    optionsUrl,
    {
      ...queryParams,
      ...exQueryParams,
    },
    {
      enabled: !!optionsUrl,
      suspense: false,
      // filtr ro'yxatlari ma'lumotnoma: 10 daqiqa eskirmaydi
      staleTime: STALE_REFERENCE,
      cacheTime: CACHE_REFERENCE,
    }
  );
  const getLabel = (option: IOption) =>
    option?.firstName
      ? `${option?.firstName} ${option?.lastName}`
      : (option?.[nameProp] as string);
  // @ts-ignore
  const OPTIONS = ((get(OptionsData, dataProp) as IOption[]) || options).map(
    (option) => ({
      ...option,
      name: getLabel(option),
    })
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const allParams = useAllQueryParams();

  const { t } = useTranslation();

  return (
    <AutoCompleteStyled multiple={multiple}>
      {label && <label>{label}</label>}
      <Autocomplete
        options={mapData ? mapData(OPTIONS) : OPTIONS}
        // @ts-ignore
        getOptionLabel={(option) => option?.name || ""}
        limitTags={1}
        onChange={(e, data) => {
          // @ts-ignore
          onChange?.(data);
          if (multiple) {
            // @ts-ignore
            setMultipleValue(data);
          } else {
            // @ts-ignore
            setValue(returnOnlyId ? data?._id! : data);
          }
          // @ts-ignore
          if (filterName && !multiple) {
            if (data) {
              setSearchParams({
                ...allParams,
                // @ts-ignore
                [filterName]: data?._id,
              });
            } else {
              delete allParams[filterName];
              setSearchParams({
                ...allParams,
              });
            }
          } else {
            if (data) {
              // @ts-ignore
              const ids = data.map((val: any) => val?._id);

              setSearchParams({
                ...allParams,
                [filterName]: JSON.stringify(ids),
              });
            } else {
              delete allParams[filterName];
              setSearchParams({
                ...allParams,
              });
            }
          }
        }}
        loading={isFetching || isDebouncing}
        disabled={disabled}
        sx={{
          "& fieldset": { border: "none" },
        }}
        multiple={multiple}
        loadingText="izlamoqda..."
        noOptionsText={!isDebouncing && "malumot topilmadi"}
        // @ts-ignore
        value={
          returnOnlyId
            ? multiple
              ? multipleValue
              : getValueFromOptions(
                  (get(OptionsData, dataProp) as IOption[]) || options,
                  value!
                )
            : value
        }
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                onChange: (e) => {
                  // @ts-ignore
                  params?.inputProps?.onChange?.(e);
                  // @ts-ignore
                  setSearch(e.target.value || "");
                },
              }}
              onBlur={() => setSearch(undefined)}
              variant="outlined"
              fullWidth
              // @ts-ignore
              placeholder={placeholder || t("LABELS.ROLE_ALL.TITLE")}
            />
          </>
        )}
      />
    </AutoCompleteStyled>
  );
}

export default AutoCompleteFilter;
